import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { FiArrowLeft, FiCheckCircle, FiAlertTriangle, FiDownload, FiInfo, FiUpload } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { importUpdateClientPhoneNumbers } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminClient.css';

const mapContactUpdateRow = (item, status, message = '') => {
    const update = item?.client_contact_update
        || item?.phone_number_update
        || {};
    return {
        reservationNumber: item?.reservation_number ?? '',
        previousFirstName: update.previous_first_name ?? '',
        firstName: update.first_name ?? item?.first_name ?? '',
        previousLastName: update.previous_last_name ?? '',
        lastName: update.last_name ?? item?.last_name ?? '',
        previousEmail: update.previous_email ?? '',
        email: update.email ?? item?.email ?? '',
        previousPhone: update.previous_phone_number ?? '',
        phone: update.phone_number ?? item?.phone_number ?? '',
        status,
        message,
    };
};

const emptyErrorRow = (message) => ({
    reservationNumber: '',
    previousFirstName: '',
    firstName: '',
    previousLastName: '',
    lastName: '',
    previousEmail: '',
    email: '',
    previousPhone: '',
    phone: '',
    status: 'error',
    message,
});

const buildResultSummary = (response) => {
    if (!response || typeof response !== 'object') {
        return { text: 'Import terminé.', isWarning: false, rows: [] };
    }

    const successCount = Number(response.successful_updates ?? 0);
    const failCount = Number(response.failed_updates ?? 0);
    const lines = [
        response.message || 'Mise à jour des contacts clients terminée.',
        `Clients mis à jour : ${successCount}`,
        `Échecs : ${failCount}`,
    ];

    const detailRows = [];

    const successes = Array.isArray(response.results?.successes)
        ? response.results.successes
        : [];
    successes.forEach((item) => {
        detailRows.push(mapContactUpdateRow(item, 'ok'));
    });

    const errors = Array.isArray(response.results?.errors)
        ? response.results.errors
        : [];
    errors.forEach((item) => {
        if (typeof item === 'string') {
            detailRows.push(emptyErrorRow(item));
            return;
        }
        detailRows.push(
            mapContactUpdateRow(item, 'error', item.error || item.message || 'Erreur')
        );
    });

    return {
        text: lines.join('\n'),
        isWarning: failCount > 0 || response.success === false,
        rows: detailRows,
        successCount,
        failCount,
    };
};

const buildExcelRows = (rows) => rows.map((row) => ({
    'N° réservation': row.reservationNumber || '',
    'Ancien prénom': row.previousFirstName || '',
    'Nouveau prénom': row.firstName || '',
    'Ancien nom': row.previousLastName || '',
    'Nouveau nom': row.lastName || '',
    'Ancien email': row.previousEmail || '',
    'Nouvel email': row.email || '',
    'Ancien téléphone': row.previousPhone || '',
    'Nouveau téléphone': row.phone || '',
    Statut: row.status === 'ok' ? 'OK' : 'Erreur',
    Message: row.message || '',
}));

const AdminClientPhoneImport = () => {
    const navigate = useNavigate();
    const [importFile, setImportFile] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState(null);
    const [resultSummary, setResultSummary] = useState(null);

    const handleExportExcel = () => {
        if (!resultSummary?.rows?.length) return;
        const worksheet = XLSX.utils.json_to_sheet(buildExcelRows(resultSummary.rows));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts clients');
        const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);
        XLSX.writeFile(workbook, `import_client_contact_${stamp}.xlsx`);
    };

    const handleFileChange = (file) => {
        setImportError(null);
        setResultSummary(null);
        if (!file) {
            setImportFile(null);
            setFileError(null);
            return;
        }
        if (!/\.(xls|xlsx)$/i.test(file.name)) {
            setImportFile(null);
            setFileError('Seuls les fichiers Excel (.xls ou .xlsx) sont acceptés.');
            return;
        }
        setImportFile(file);
        setFileError(null);
    };

    const handleReset = () => {
        setImportFile(null);
        setFileError(null);
        setImportError(null);
        setResultSummary(null);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!importFile) {
            setFileError('Veuillez sélectionner un fichier Excel.');
            return;
        }

        setImportLoading(true);
        setImportError(null);
        setResultSummary(null);

        try {
            const response = await importUpdateClientPhoneNumbers(importFile);
            const hasResultShape = response
                && (
                    response.successful_updates != null
                    || response.failed_updates != null
                    || response.results
                );
            if (!hasResultShape && (response?.detail || response?.success === false)) {
                setImportError(
                    typeof response.detail === 'string'
                        ? response.detail
                        : handleApiError({ response: { data: response } }, 'Erreur lors de l\'import')
                );
            } else {
                setResultSummary(buildResultSummary(response));
            }
        } catch (error) {
            setImportError(handleApiError(error, 'Erreur lors de l\'import des numéros de téléphone'));
        } finally {
            setImportLoading(false);
        }
    };

    return (
        <div className="dashboard-page admin-client-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiUpload className="me-2" aria-hidden />
                                Import modifier client
                            </h2>
                            <p className="text-muted mb-0">
                                Mettre à jour le prénom, le nom, l&apos;email et le téléphone des clients depuis un fichier Excel.
                            </p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/admin/client')}>
                            <FiArrowLeft className="me-2" aria-hidden /> Retour
                        </Button>
                    </div>

                    <Alert variant="light" className="border mb-4">
                        <div className="d-flex align-items-start gap-2">
                            <FiInfo className="mt-1 flex-shrink-0" aria-hidden />
                            <div>
                                <strong>Endpoint</strong>
                                <div className="small text-muted mb-2">
                                    <code>PATCH /api/import/clients/phone-number</code>
                                </div>
                                <strong>Votre fichier Excel doit contenir les colonnes suivantes :</strong>
                                <ul className="mb-0 mt-2 small">
                                    <li><code>reservation_number</code></li>
                                    <li><code>first_name</code></li>
                                    <li><code>last_name</code></li>
                                    <li><code>email</code></li>
                                    <li><code>phone_number</code></li>
                                </ul>
                            </div>
                        </div>
                    </Alert>

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Fichier Excel <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                        isInvalid={!!fileError}
                                        onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                                        disabled={importLoading}
                                    />
                                    <Form.Text className="text-muted">
                                        Fichiers acceptés : .xls, .xlsx
                                    </Form.Text>
                                    {fileError && (
                                        <Form.Control.Feedback type="invalid" className="d-block">
                                            {fileError}
                                        </Form.Control.Feedback>
                                    )}
                                    {importFile && !fileError && (
                                        <div className="mt-2 small text-success">
                                            ✓ Fichier sélectionné : {importFile.name}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={handleReset}
                                disabled={importLoading}
                            >
                                Réinitialiser
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={importLoading || !importFile}
                            >
                                {importLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Import en cours…
                                    </>
                                ) : (
                                    <>
                                        <FiUpload className="me-2" aria-hidden />
                                        Importer
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>

                    {importLoading && (
                        <Alert variant="info" className="mt-4 mb-0">
                            <Spinner size="sm" animation="border" className="me-2" />
                            <strong>Import en cours…</strong> Veuillez patienter et ne pas fermer cette page.
                        </Alert>
                    )}

                    {importError && (
                        <Alert
                            variant="danger"
                            className="mt-4"
                            dismissible
                            onClose={() => setImportError(null)}
                        >
                            {importError}
                        </Alert>
                    )}

                    {resultSummary && (
                        <Alert
                            variant={resultSummary.isWarning ? 'warning' : 'success'}
                            className="mt-4"
                            dismissible
                            onClose={() => setResultSummary(null)}
                        >
                            <div className="d-flex align-items-start gap-2 mb-2">
                                {resultSummary.isWarning
                                    ? <FiAlertTriangle className="mt-1 flex-shrink-0" aria-hidden />
                                    : <FiCheckCircle className="mt-1 flex-shrink-0" aria-hidden />}
                                <div>
                                    <Alert.Heading className="h6 mb-1">Résultat de l&apos;import</Alert.Heading>
                                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                                        {resultSummary.text}
                                    </pre>
                                </div>
                            </div>

                            {resultSummary.rows.length > 0 && (
                                <>
                                    <div className="d-flex justify-content-end mb-2">
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            className="d-flex align-items-center"
                                            onClick={handleExportExcel}
                                        >
                                            <FiDownload className="me-2" aria-hidden />
                                            Exporter Excel
                                        </Button>
                                    </div>
                                    <div className="table-responsive mt-2">
                                        <Table striped bordered hover size="sm" className="mb-0 bg-white">
                                            <thead>
                                                <tr>
                                                    <th>N° réservation</th>
                                                    <th>Ancien prénom</th>
                                                    <th>Nouveau prénom</th>
                                                    <th>Ancien nom</th>
                                                    <th>Nouveau nom</th>
                                                    <th>Ancien email</th>
                                                    <th>Nouvel email</th>
                                                    <th>Ancien téléphone</th>
                                                    <th>Nouveau téléphone</th>
                                                    <th>Statut</th>
                                                    <th>Message</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {resultSummary.rows.map((row, index) => (
                                                    <tr key={`${row.reservationNumber}-${row.email}-${index}`}>
                                                        <td>
                                                            {row.reservationNumber
                                                                ? <code>{row.reservationNumber}</code>
                                                                : '—'}
                                                        </td>
                                                        <td>{row.previousFirstName || '—'}</td>
                                                        <td>{row.firstName || '—'}</td>
                                                        <td>{row.previousLastName || '—'}</td>
                                                        <td>{row.lastName || '—'}</td>
                                                        <td>{row.previousEmail || '—'}</td>
                                                        <td>{row.email || '—'}</td>
                                                        <td>{row.previousPhone || '—'}</td>
                                                        <td>{row.phone || '—'}</td>
                                                        <td>{row.status === 'ok' ? 'OK' : 'Erreur'}</td>
                                                        <td>{row.message || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </Alert>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminClientPhoneImport;
